import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import METRIC_DEFINITIONS from './metricDefinitions';
import {
  DRILLDOWN_SHEET_BODY_SCROLL_SX,
  DRILLDOWN_SHEET_BORDER_RADIUS,
  DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
} from './analyticsDrilldownStyles';

/**
 * EditorEngagementCard
 *
 * Three stats derived entirely from the /timeseries feedRows payload —
 * zero extra API calls. All three are computed only on "interactive sessions":
 * editor_opened → editor_closed pairs within a 4-hour window where at least
 * one meaningful action was taken (furnish, shuffle, or item add).
 *
 * Pairing key:
 *   meta.guestSessionId  (populated once the FE guestSessionId update ships)
 *   || meta.user          (fallback for events logged before guestSessionId)
 *
 * Stats:
 *   • Typical session   — median(editor_closed.ts − editor_opened.ts) [headline]
 *   • Avg in editor     — mean(editor_closed.ts − editor_opened.ts)   [secondary, shown
 *                         alongside median so the skew from long sessions stays visible]
 *   • Made edits        — % of interactive sessions with ≥1 furnish/shuffle/add-items
 *   • Shuffles/session  — mean shuffle_room count per interactive session
 *
 * Each interactive session is also assigned a bucket (quick / brief / standard /
 * long) so the card can show distribution alongside the headline number.
 * Bucket boundaries live in SESSION_BUCKETS above.
 *
 * Backward compatibility: all previously returned fields (sessionCount, measuredCount,
 * avgDurationSeconds, madeEditsRate, avgShufflesPerSession, sessions) are preserved
 * with identical semantics. New fields are additive only.
 */

const SESSION_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours
const ENGAGEMENT_EVENTS = new Set(['furnish_room', 'shuffle_room', 'add_items_manually']);

// When a user shuffles, layoutData._id changes and fires a new editor_opened,
// fragmenting a single editing session into multiple raw sessions. Any consecutive
// sessions from the same user whose gap is ≤ MERGE_GAP_MS are merged into one.
const MERGE_GAP_MS = 2 * 60 * 1000; // 2 minutes

// ── New interactive-session model (active time) ──────────────────────────
// A "visit" is the umbrella unit: it begins the first time the user opens
// the editor and ends at their final recorded event. Multiple editor opens
// and the gallery time sandwiched between them all belong to ONE visit.
// Any gap longer than IDLE_THRESHOLD between consecutive events is treated
// as idle (stepped away, screen locked, etc.) and removed from the active
// duration. A gap longer than VISIT_BOUNDARY_GAP_MS starts a new visit.
//
// All three constants are intentionally generous on the upper end so we
// don't fragment legitimate iterative editing. They're locked here so we
// can revisit with data; partners only see the resulting metric.
const VISIT_BOUNDARY_GAP_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_THRESHOLD_MS = 2 * 60 * 1000;      // 2 minutes
const INTERACTIVE_MIN_ACTIVE_MS = 5000;       // mirrors the existing 5s floor

// All events that "count" toward an interactive visit. webview_opened is
// included so gallery time sandwiched between editor sessions can be part
// of the active duration (per the visit definition above).
const VISIT_RELEVANT_EVENTS = new Set([
  'webview_opened',
  'editor_opened',
  'editor_closed',
  'furnish_room',
  'shuffle_room',
  'add_items_manually',
]);

// Session length buckets. Boundaries are exclusive upper bounds in seconds.
// Order matters — bucketForDuration walks the list and stops at the first match.
//
// Calibration history:
//   • Original 5-bucket model topped out at 30+ min ("outlier"), which was
//     useful when we were validating that idle subtraction worked but no
//     longer reflects real-world usage. The "outlier" bucket has been
//     removed; consumers reading `bucketCounts.outlier` will see 0.
//   • Current 4-bucket model is calibrated to observed staging data
//     where the longest interactive sessions land in the 12–20 min range.
//     The top bucket is "15+ min" so genuinely long engagement is its own
//     visible bar (the marketing story) rather than being hidden inside
//     a "long session" bucket that ends at 30 min.
//
// All durations are ACTIVE TIME (gaps over IDLE_THRESHOLD_MS are removed),
// not wall-clock — see EditorEngagementCard.js header.
const SESSION_BUCKETS = [
  { key: 'quick',    label: 'Quick peek',      range: 'Under 1 min', maxSeconds: 60 },
  { key: 'brief',    label: 'Browsed',         range: '1–5 min',     maxSeconds: 300 },
  { key: 'standard', label: 'Engaged session', range: '5–15 min',    maxSeconds: 900 },
  { key: 'long',     label: 'Long session',    range: '15+ min',     maxSeconds: Infinity },
];

const BUCKET_FILL = {
  quick: 'rgba(0,0,0,0.20)',
  brief: 'rgba(0,0,0,0.35)',
  standard: 'rgba(0,0,0,0.55)',
  long: 'rgba(0,0,0,0.80)',
};

const MIN_BAR_HEIGHT_PX = 10;

/** Drill-down bucket chips reuse chart fill tokens. */
const BUCKET_COLORS = BUCKET_FILL;

// Industry benchmark for a typical 3D-tour visit (~90s). Used by the page
// hero comparative bar (EngagementHeroStrip) so partners can see at a glance
// how MagicFurnish median session time compares.
export const BENCHMARK_3D_TOUR_SECONDS = 90;

// Global `button { text-transform: capitalize }` in index.css affects ButtonBase
// cards — reset for this component tree only.
const NO_AUTO_CAPS = {
  textTransform: 'none',
  '& .MuiTypography-root': { textTransform: 'none' },
  '& span': { textTransform: 'none' },
};

const bucketForDuration = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  // The last bucket has maxSeconds: Infinity, so a finite duration always
  // matches one of the buckets. The nullish fallback is purely defensive
  // and intentionally targets the final bucket key so a future change to
  // boundaries can never silently drop counts to a non-existent key.
  return (
    SESSION_BUCKETS.find((b) => seconds < b.maxSeconds)?.key ??
    SESSION_BUCKETS[SESSION_BUCKETS.length - 1].key
  );
};

// Median of a numeric array. Returns null on empty input. Rounds the
// average-of-two-middles for even-length arrays to keep the value an integer.
const median = (nums) => {
  if (!Array.isArray(nums) || nums.length === 0) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    : sorted[mid];
};

const formatDuration = (secs) => {
  if (secs == null) return '—';
  if (secs < 60) return `~${secs}s`;
  return `~${Math.round(secs / 60)}m`;
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const useEditorStats = (feedRowsInput, excludeUsers = null) =>
  useMemo(() => {
    // Apply the optional insider exclusion at the top so the entire body
    // below — which references `feedRows` throughout — sees the filtered
    // set. Default behavior (excludeUsers null/empty) is unchanged so
    // existing callers don't break.
    const hasExclusions = excludeUsers && excludeUsers.size > 0;
    const feedRows = hasExclusions
      ? (Array.isArray(feedRowsInput) ? feedRowsInput : []).filter((row) => {
          const u = row?.meta?.user;
          return !(u && excludeUsers.has(String(u)));
        })
      : feedRowsInput;
    const emptyBucketCounts = SESSION_BUCKETS.reduce((acc, b) => {
      acc[b.key] = 0;
      return acc;
    }, {});

    const empty = {
      sessionCount: 0,
      measuredCount: 0,
      avgDurationSeconds: null,
      medianDurationSeconds: null,
      madeEditsRate: null,
      avgShufflesPerSession: null,
      sessions: [],
      bucketCounts: emptyBucketCounts,
      outlierCount: 0,
      // New-model fields (active time / umbrella interactive visit). Always
      // returned so consumers can destructure safely on empty input.
      interactiveActiveCount: 0,
      interactiveActiveMedianSeconds: null,
      interactiveActiveAvgSeconds: null,
      editorVisitsPerInteractive: null,
      interactiveVisits: [],
    };

    if (!Array.isArray(feedRows) || feedRows.length === 0) return empty;

    // ── Group relevant events by session key ──────────────────────────────
    const buckets = new Map();
    feedRows.forEach((row) => {
      const event = row?.meta?.event;
      if (!event) return;
      if (
        event !== 'editor_opened' &&
        event !== 'editor_closed' &&
        !ENGAGEMENT_EVENTS.has(event)
      ) return;

      const key =
        row?.meta?.guestSessionId ||
        (row?.meta?.user &&
          row.meta.user !== 'guest' &&
          row.meta.user !== 'anonymous'
          ? row.meta.user
          : null);
      if (!key) return;

      if (!buckets.has(key)) buckets.set(key, { key, rows: [] });
      buckets.get(key).rows.push(row);
    });

    // ── Build raw sessions: editor_opened → next editor_closed within window ─
    // Each shuffle fires a new editor_opened (layoutData._id changes), so one
    // real editing visit may produce many raw sessions. The merge pass below
    // collapses them into logical visits.
    const rawSessions = [];
    buckets.forEach(({ key, rows }) => {
      const sorted = [...rows].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );

      sorted
        .filter((r) => r.meta?.event === 'editor_opened')
        .forEach((openRow) => {
          const openMs = new Date(openRow.timestamp).getTime();
          if (!Number.isFinite(openMs)) return;

          const closeRow = sorted.find((r) => {
            if (r.meta?.event !== 'editor_closed') return false;
            const ts = new Date(r.timestamp).getTime();
            return ts > openMs && ts - openMs <= SESSION_WINDOW_MS;
          });

          const closeMs = closeRow ? new Date(closeRow.timestamp).getTime() : null;
          const windowEnd = closeMs ?? openMs + SESSION_WINDOW_MS;

          const windowEvents = sorted.filter((r) => {
            const ts = new Date(r.timestamp).getTime();
            return ts > openMs && ts <= windowEnd;
          });

          const actionLabels = [];
          if (windowEvents.some((r) => r.meta?.event === 'furnish_room')) actionLabels.push('auto-furnished');
          if (windowEvents.some((r) => r.meta?.event === 'shuffle_room')) actionLabels.push('shuffled');
          if (windowEvents.some((r) => r.meta?.event === 'add_items_manually')) actionLabels.push('added items');

          // Idle-subtracted "active" duration for the same session. The
          // bucket bar and the headline median/avg were both showing
          // wall-clock time before, which produced 100 + minute sessions
          // for users who left the tab open. We now subtract any gap
          // longer than IDLE_THRESHOLD_MS between consecutive recorded
          // events. This matches the same rule the new-model Active
          // Engagement section uses, so the two halves of the card now
          // agree on what counts as active time.
          let activeMs = null;
          if (closeMs != null) {
            const eventTimes = [
              openMs,
              ...windowEvents
                .map((r) => new Date(r.timestamp).getTime())
                .filter(Number.isFinite),
              closeMs,
            ].sort((a, b) => a - b);
            let idleTotal = 0;
            for (let i = 1; i < eventTimes.length; i++) {
              const gap = eventTimes[i] - eventTimes[i - 1];
              if (gap > IDLE_THRESHOLD_MS) idleTotal += gap - IDLE_THRESHOLD_MS;
            }
            activeMs = Math.max(0, closeMs - openMs - idleTotal);
          }

          rawSessions.push({
            key,
            openTs: openRow.timestamp,        // ISO string, for display
            _openMs: openMs,                  // ms number, for merge arithmetic
            closeTs: closeMs,                 // ms number or null
            wallClockDuration: closeMs ? closeMs - openMs : null,
            duration: activeMs,               // idle-subtracted; consumed by median/avg/bucket
            hasEngagement: windowEvents.some((r) => ENGAGEMENT_EVENTS.has(r.meta?.event)),
            shuffleCount: windowEvents.filter((r) => r.meta?.event === 'shuffle_room').length,
            actionLabels,
          });
        });
    });

    if (rawSessions.length === 0) return empty;

    // ── Merge consecutive sessions per user into logical visits ───────────
    // When sessions overlap (shared editor_closed) the earliest open's window
    // is already a superset, so we don't double-count shuffles — we just extend
    // the close timestamp. When sessions are disjoint but within MERGE_GAP_MS
    // (e.g. user pressed Done then immediately came back), we sum the counts.
    const sessionsByKey = new Map();
    rawSessions.forEach((s) => {
      if (!sessionsByKey.has(s.key)) sessionsByKey.set(s.key, []);
      sessionsByKey.get(s.key).push(s);
    });

    const sessions = [];
    sessionsByKey.forEach((userSessions) => {
      const byTime = [...userSessions].sort((a, b) => a._openMs - b._openMs);
      const acc = [];

      byTime.forEach((curr) => {
        if (acc.length === 0) {
          acc.push({ ...curr });
          return;
        }

        const prev = acc[acc.length - 1];
        const prevCloseMs = prev.closeTs;
        const gap = curr._openMs - (prevCloseMs ?? (prev._openMs + SESSION_WINDOW_MS));

        if (gap <= MERGE_GAP_MS) {
          const overlaps = prevCloseMs != null && curr._openMs < prevCloseMs;
          if (overlaps) {
            // prev's window covers curr's — don't add shuffles/actions again.
            // Just extend the close boundary if curr ends later. duration
            // (active time) tracks the longer of the two — the smaller
            // active range is contained inside the larger one, so the max
            // is the correct merged value.
            if (curr.closeTs != null && (prevCloseMs == null || curr.closeTs > prevCloseMs)) {
              prev.closeTs = curr.closeTs;
              prev.wallClockDuration = prev.closeTs - prev._openMs;
              if (curr.duration != null && (prev.duration == null || curr.duration > prev.duration)) {
                prev.duration = curr.duration;
              }
            }
          } else {
            // Disjoint but adjacent: accumulate counts from both segments.
            // Active durations are additive (the inter-session gap itself
            // is already filtered by the MERGE_GAP_MS check above, so we
            // only credit the time each segment was actually open).
            prev.hasEngagement = prev.hasEngagement || curr.hasEngagement;
            prev.shuffleCount += curr.shuffleCount;
            prev.actionLabels = [...new Set([...prev.actionLabels, ...curr.actionLabels])];
            if (curr.closeTs != null) {
              prev.closeTs = curr.closeTs;
              prev.wallClockDuration = prev.closeTs - prev._openMs;
            }
            if (curr.duration != null) {
              prev.duration = (prev.duration ?? 0) + curr.duration;
            }
          }
        } else {
          acc.push({ ...curr });
        }
      });

      acc.forEach(({ _openMs, ...rest }) => sessions.push(rest));
    });

    // ── Compute aggregate stats from merged sessions ───────────────────────
    // All cleanly-paired sessions (> 5 s). Used as denominator for madeEditsRate.
    const allPaired = sessions.filter((s) => s.duration != null && s.duration > 5000);

    // Engagement sessions: paired sessions where ≥1 meaningful action fired.
    const engagementSessions = allPaired.filter((s) => s.hasEngagement);

    const avgDurationSeconds =
      engagementSessions.length > 0
        ? Math.round(
            engagementSessions.reduce((sum, s) => sum + s.duration, 0) /
              engagementSessions.length /
              1000
          )
        : null;

    // Median is computed over the same interactive set as the average so the
    // two numbers are directly comparable. Because the bucket boundaries are
    // expressed in seconds, durations are converted once here and reused below.
    const engagementDurationsSec = engagementSessions.map((s) =>
      Math.round(s.duration / 1000)
    );
    const medianDurationSeconds = median(engagementDurationsSec);

    // Bucket counts are computed over the FULL interactive set, not the sliced
    // top-30 used for the drill-down detail rows, so the card shows accurate
    // distribution totals even when individual session rows are paginated.
    const bucketCounts = SESSION_BUCKETS.reduce((acc, b) => {
      acc[b.key] = 0;
      return acc;
    }, {});
    engagementDurationsSec.forEach((secs) => {
      const key = bucketForDuration(secs);
      if (key && bucketCounts[key] != null) bucketCounts[key] += 1;
    });

    const madeEditsRate =
      allPaired.length > 0
        ? Math.round((engagementSessions.length * 100) / allPaired.length)
        : null;

    const avgShufflesPerSession =
      engagementSessions.length > 0
        ? parseFloat(
            (
              engagementSessions.reduce((sum, s) => sum + s.shuffleCount, 0) /
              engagementSessions.length
            ).toFixed(1)
          )
        : null;

    // Stamp each merged session with its bucket so the drill-down can group
    // without recomputing. Sessions without paired duration get bucket=null
    // and naturally fall outside the engagement filter the drill-down applies.
    const sessionsWithBucket = sessions.map((s) => {
      const secs = s.duration != null ? Math.round(s.duration / 1000) : null;
      return { ...s, durationSeconds: secs, bucket: bucketForDuration(secs) };
    });

    // ── New interactive-session model (active time) ──────────────────────
    // Independent pass over feedRows so it stays trivially revertible. Builds
    // umbrella "visits" per user that span multiple editor opens + sandwiched
    // gallery time, with idle gaps removed.
    //
    // Each visit carries everything the card + drill-down need to render —
    // action labels (auto-furnished, shuffled, added items), shuffle count,
    // user/guest key, and the first editor-open timestamp — so the bucket
    // bar and the per-row breakdown are driven from the same model and the
    // totals always agree with the headline count.
    const visitBuckets = new Map();
    feedRows.forEach((row) => {
      const event = row?.meta?.event;
      if (!event || !VISIT_RELEVANT_EVENTS.has(event)) return;
      const key =
        row?.meta?.guestSessionId ||
        (row?.meta?.user &&
          row.meta.user !== 'guest' &&
          row.meta.user !== 'anonymous'
          ? row.meta.user
          : null);
      if (!key) return;
      const ts = new Date(row.timestamp).getTime();
      if (!Number.isFinite(ts)) return;
      if (!visitBuckets.has(key)) visitBuckets.set(key, []);
      visitBuckets.get(key).push({ ts, event, isoTs: row.timestamp });
    });

    const visits = [];
    visitBuckets.forEach((evts, key) => {
      const sortedEvts = [...evts].sort((a, b) => a.ts - b.ts);

      // Walk through events accumulating "candidates". A gap larger than
      // VISIT_BOUNDARY_GAP_MS closes the current candidate and starts a new
      // one. Only candidates that contain an editor_opened become visits.
      let current = [];
      const candidates = [];
      sortedEvts.forEach((e) => {
        if (current.length === 0) {
          current = [e];
          return;
        }
        const gap = e.ts - current[current.length - 1].ts;
        if (gap > VISIT_BOUNDARY_GAP_MS) {
          candidates.push(current);
          current = [e];
        } else {
          current.push(e);
        }
      });
      if (current.length > 0) candidates.push(current);

      candidates.forEach((cand) => {
        const firstEditorIdx = cand.findIndex((e) => e.event === 'editor_opened');
        if (firstEditorIdx === -1) return; // no editor activity → not a visit

        const visitEvents = cand.slice(firstEditorIdx);
        const start = visitEvents[0].ts;
        const startIso = visitEvents[0].isoTs;
        const end = visitEvents[visitEvents.length - 1].ts;
        // end < start would mean the events are not sorted (they are) — guard
        // is defensive only. end === start (a single-instant visit) is allowed
        // through; it produces activeMs = 0 and is then filtered out by the
        // INTERACTIVE_MIN_ACTIVE_MS floor below, same as a 0-duration
        // wall-clock session would be.
        if (end < start) return;

        let idleTotal = 0;
        for (let i = 1; i < visitEvents.length; i += 1) {
          const gap = visitEvents[i].ts - visitEvents[i - 1].ts;
          if (gap > IDLE_THRESHOLD_MS) idleTotal += gap - IDLE_THRESHOLD_MS;
        }

        const activeMs = Math.max(0, end - start - idleTotal);
        const editorOpens = visitEvents.filter((e) => e.event === 'editor_opened').length;
        const hasEngagement = visitEvents.some((e) => ENGAGEMENT_EVENTS.has(e.event));
        const shuffleCount = visitEvents.filter((e) => e.event === 'shuffle_room').length;
        const actionLabels = [];
        if (visitEvents.some((e) => e.event === 'furnish_room')) actionLabels.push('auto-furnished');
        if (visitEvents.some((e) => e.event === 'shuffle_room')) actionLabels.push('shuffled');
        if (visitEvents.some((e) => e.event === 'add_items_manually')) actionLabels.push('added items');

        visits.push({
          key,
          startTs: start,
          startTsIso: startIso,
          endTs: end,
          activeMs,
          editorOpens,
          hasEngagement,
          eventCount: visitEvents.length,
          shuffleCount,
          actionLabels,
        });
      });
    });

    const interactiveVisits = visits.filter(
      (v) => v.hasEngagement && v.activeMs >= INTERACTIVE_MIN_ACTIVE_MS
    );
    const activeSecs = interactiveVisits.map((v) => Math.round(v.activeMs / 1000));
    const interactiveActiveAvgSeconds =
      activeSecs.length > 0
        ? Math.round(activeSecs.reduce((a, b) => a + b, 0) / activeSecs.length)
        : null;
    const interactiveActiveMedianSeconds = median(activeSecs);
    const editorVisitsPerInteractive =
      interactiveVisits.length > 0
        ? parseFloat(
            (
              interactiveVisits.reduce((sum, v) => sum + v.editorOpens, 0) /
              interactiveVisits.length
            ).toFixed(1)
          )
        : null;

    // Bucket totals are now computed from the same `interactiveVisits` set
    // that powers the headline, so the stacked bar segments always sum to
    // the headline count. Previously the bar drove off `engagementSessions`
    // (raw editor_opened → editor_closed pairs, OLD model) while the
    // headline drove off umbrella visits (NEW model), producing the "10 in
    // header / 3+1+1=5 in bar" mismatch partners reported.
    const interactiveBucketCounts = SESSION_BUCKETS.reduce((acc, b) => {
      acc[b.key] = 0;
      return acc;
    }, {});
    activeSecs.forEach((secs) => {
      const k = bucketForDuration(secs);
      if (k && interactiveBucketCounts[k] != null) interactiveBucketCounts[k] += 1;
    });

    // Render-ready visit objects for the drill-down. Shape matches the old
    // `sessions` array so the existing row renderer doesn't need a rewrite —
    // it reads `key`, `openTs`, `duration`, `actionLabels`, `shuffleCount`,
    // `bucket`, `hasEngagement`, `durationSeconds`.
    const interactiveVisitsForDrill = interactiveVisits
      .map((v) => {
        const secs = Math.round(v.activeMs / 1000);
        return {
          key: v.key,
          openTs: v.startTsIso || new Date(v.startTs).toISOString(),
          duration: v.activeMs,
          durationSeconds: secs,
          bucket: bucketForDuration(secs),
          hasEngagement: v.hasEngagement,
          shuffleCount: v.shuffleCount || 0,
          actionLabels: v.actionLabels || [],
          editorOpens: v.editorOpens || 1,
        };
      })
      .sort((a, b) => new Date(b.openTs) - new Date(a.openTs))
      .slice(0, 30);

    return {
      sessionCount: sessions.length,
      measuredCount: engagementSessions.length,
      avgDurationSeconds,
      medianDurationSeconds,
      madeEditsRate,
      avgShufflesPerSession,
      // `sessions` now mirrors the visit-bucketed list so the drill-down
      // and the bucket bar always show the same N. The pair-based
      // `sessionsWithBucket` from the OLD model is preserved on
      // `legacyPairedSessions` for any internal debugging needs.
      sessions: interactiveVisitsForDrill,
      legacyPairedSessions: sessionsWithBucket
        .sort((a, b) => new Date(b.openTs) - new Date(a.openTs))
        .slice(0, 30),
      bucketCounts: interactiveBucketCounts,
      outlierCount: interactiveBucketCounts.outlier || 0,
      legacyBucketCounts: bucketCounts,
      interactiveActiveCount: interactiveVisits.length,
      interactiveActiveMedianSeconds,
      interactiveActiveAvgSeconds,
      editorVisitsPerInteractive,
      interactiveVisits: interactiveVisits
        .sort((a, b) => b.startTs - a.startTs)
        .slice(0, 30),
    };
  }, [feedRowsInput, excludeUsers]);

const formatSessionDuration = (ms) => {
  if (!ms) return 'open ended';
  const totalSecs = Math.round(ms / 1000);
  if (totalSecs < 60) return `${totalSecs}s`;
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

// Definitions live in metricDefinitions.js and are rendered via <MetricTooltip />.

const StatBlock = ({ value, label, size = 'card' }) => (
  <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
    <Typography
      sx={{
        fontSize: size === 'card' ? { xs: '1.375rem', sm: '1.625rem' } : { xs: '1.25rem', sm: '1.375rem' },
        fontWeight: 600,
        color: 'rgba(0,0,0,0.88)',
        lineHeight: 1,
        letterSpacing: '-0.025em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: size === 'card' ? '0.7rem' : '0.68rem',
        color: 'rgba(0,0,0,0.42)',
        mt: 0.35,
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Headline row: sessions → median → average (left to right). */
const InteractiveSessionStatsRow = ({
  sessionCount,
  medianSeconds,
  avgSeconds,
  size = 'card',
}) => (
  <Stack
    direction="row"
    spacing={{ xs: 2, sm: 2.5, md: 3 }}
    alignItems="flex-start"
    sx={{ width: '100%' }}
  >
    <StatBlock
      value={sessionCount.toLocaleString()}
      label="interactive sessions"
      size={size}
    />
    {medianSeconds != null && (
      <StatBlock
        value={formatDuration(medianSeconds)}
        label="typical (median) duration"
        size={size}
      />
    )}
    {avgSeconds != null && (
      <StatBlock
        value={formatDuration(avgSeconds)}
        label="average duration"
        size={size}
      />
    )}
  </Stack>
);

/**
 * Vertical bar chart — categorical duration distribution (time left → right).
 * Flex-column layout; bars sit on a shared baseline without per-column tracks.
 */
export const DurationDistributionChart = ({ bucketCounts, total }) => {
  if (!total || total <= 0) return null;

  const counts = SESSION_BUCKETS.map((b) => bucketCounts?.[b.key] ?? 0);
  const maxCount = Math.max(1, ...counts);

  return (
    <Box
      role="img"
      aria-label="Session length distribution"
      sx={{ mt: { xs: 2, sm: 2.25 }, width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 1.75 },
          width: '100%',
          height: { xs: 80, sm: 96, md: 104 },
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {SESSION_BUCKETS.map((bucket, i) => {
          const count = counts[i];
          const fillPct = count > 0 ? (count / maxCount) * 100 : 0;

          return (
            <Box
              key={bucket.key}
              sx={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              {count > 0 && (
                <Box
                  sx={{
                    width: { xs: '62%', sm: '58%' },
                    maxWidth: 44,
                    height: `${fillPct}%`,
                    minHeight: MIN_BAR_HEIGHT_PX,
                    bgcolor: BUCKET_FILL[bucket.key],
                    borderRadius: '5px 5px 0 0',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 1.75 },
          width: '100%',
          mt: 0.75,
        }}
      >
        {SESSION_BUCKETS.map((bucket, i) => {
          const count = counts[i];
          return (
            <Box
              key={bucket.key}
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  fontWeight: 600,
                  color: count > 0 ? 'rgba(0,0,0,0.62)' : 'rgba(0,0,0,0.28)',
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {count}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.6875rem', sm: '0.7rem' },
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.55)',
                  lineHeight: 1.25,
                  mt: 0.35,
                }}
              >
                {bucket.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.625rem', sm: '0.65rem' },
                  color: 'rgba(0,0,0,0.38)',
                  lineHeight: 1.25,
                  mt: 0.15,
                }}
              >
                {bucket.range}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const EditorEngagementCard = ({ feedRows = [], loading = false, drillOpen: externalOpen, onDrillClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalOpen, setInternalOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(true);
  // Support both internal (card click) and external (Shuffles footnote) open triggers.
  const isOpen = Boolean(externalOpen) || internalOpen;
  const handleClose = () => {
    setInternalOpen(false);
    onDrillClose?.();
  };

  // avgShufflesPerSession is still returned by useEditorStats for
  // backward compat (AnalyticsTwo's Shuffles card consumed it previously)
  // but is no longer rendered here — the Shuffles card now shows total
  // shuffles + the canonical interactive-session count, which is more
  // intuitive for partners.
  //
  // `outlierCount` is no longer destructured (the dedicated 30+ min bucket
  // was removed in favor of a 15+ min "Long session" bucket inside the new
  // distribution chart). `useEditorStats` still returns `outlierCount: 0`
  // for any downstream consumer that still reads the field.
  const {
    measuredCount,
    sessions,
    bucketCounts,
    interactiveActiveCount,
    interactiveActiveMedianSeconds,
    interactiveActiveAvgSeconds,
  } = useEditorStats(feedRows);

  return (
    <>
      {loading ? (
        <Skeleton
          variant="rounded"
          height={220}
          sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)', mt: { xs: 1.5, sm: 2 } }}
        />
      ) : (
        <ButtonBase
          onClick={interactiveActiveCount > 0 ? () => setInternalOpen(true) : undefined}
          disabled={interactiveActiveCount === 0}
          sx={{
            ...NO_AUTO_CAPS,
            position: 'relative',
            width: '100%',
            textAlign: 'left',
            display: 'block',
            borderRadius: '14px !important',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: '#fff',
            p: { xs: 1.75, sm: 2 },
            mt: { xs: 1.5, sm: 2 },
            transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
            '&:hover': interactiveActiveCount > 0
              ? { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' }
              : {},
            '&:active': interactiveActiveCount > 0 ? { transform: 'scale(0.997)' } : {},
            cursor: interactiveActiveCount > 0 ? 'pointer' : 'default',
          }}
        >
          {/* Header. The right-side "N interactive" tag and the "i" tooltip
              are intentionally not rendered on the card itself anymore —
              both move into the drill-down modal so the card stays
              minimal and the whole tile is a clean tap target on mobile. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: { xs: 1, sm: 1.25 } }}
          >
            <TouchAppOutlinedIcon sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
            <Typography
              sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(0,0,0,0.5)' }}
            >
              Interactive Sessions
            </Typography>
          </Stack>

          {/* Body */}
          {interactiveActiveCount === 0 ? (
            <Typography
              sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.35)', lineHeight: 1.5 }}
            >
              No interactive sessions in this period.
            </Typography>
          ) : (
            <>
              <InteractiveSessionStatsRow
                sessionCount={interactiveActiveCount}
                medianSeconds={interactiveActiveMedianSeconds}
                avgSeconds={interactiveActiveAvgSeconds}
                size="card"
              />

              <DurationDistributionChart
                bucketCounts={bucketCounts}
                total={interactiveActiveCount || measuredCount}
              />
            </>
          )}

          {/* Chevron */}
          {interactiveActiveCount > 0 && (
            <ChevronRightIcon
              sx={{
                position: 'absolute',
                top: { xs: 15, sm: 19 },
                right: { xs: 11, sm: 15 },
                fontSize: 18,
                color: 'rgba(0,0,0,0.3)',
                opacity: 0.45,
                pointerEvents: 'none',
              }}
            />
          )}
        </ButtonBase>
      )}

      {/* Drill-down modal — layout aligned with MetricDrillModal for mobile */}
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: DRILLDOWN_SHEET_BORDER_RADIUS,
            ...DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
            m: { xs: 0, sm: 2 },
            mt: { xs: 'auto', sm: 2 },
            maxHeight: { xs: '92vh', sm: '85vh' },
            bgcolor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 2.5, sm: 3 },
            pb: 2,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '1.05rem',
                color: 'rgba(0,0,0,0.9)',
                lineHeight: 1.2,
              }}
            >
              Interactive Sessions
            </Typography>
            <IconButton size="small" onClick={handleClose} sx={{ mt: 0, mr: -0.5, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            ...DRILLDOWN_SHEET_BODY_SCROLL_SX,
            ...NO_AUTO_CAPS,
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, sm: 3 },
              pt: 2,
              paddingBottom: {
                xs: 'calc(env(safe-area-inset-bottom) + 24px)',
                sm: 3,
              },
            }}
          >
          {METRIC_DEFINITIONS.editorSessionsCard && (
            <Box
              sx={{
                mb: 2,
                p: { xs: 1.5, sm: 1.75 },
                borderRadius: 2,
                bgcolor: 'rgba(0,0,0,0.025)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography
                sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.66)', lineHeight: 1.55 }}
              >
                {METRIC_DEFINITIONS.editorSessionsCard.body}
              </Typography>
              {(METRIC_DEFINITIONS.editorSessionsCard.sections || []).map((s, i) => (
                <Box key={`${s.label}-${i}`} sx={{ mt: 1.25 }}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: 'rgba(0,0,0,0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      mb: 0.4,
                    }}
                  >
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.66)', lineHeight: 1.55 }}
                  >
                    {s.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mb: 1.5 }}>
            <InteractiveSessionStatsRow
              sessionCount={interactiveActiveCount}
              medianSeconds={interactiveActiveMedianSeconds}
              avgSeconds={interactiveActiveAvgSeconds}
              size="drill"
            />
          </Box>

          <DurationDistributionChart
            bucketCounts={bucketCounts}
            total={interactiveActiveCount || measuredCount}
          />

          <ButtonBase
            onClick={() => setSessionsOpen((v) => !v)}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              py: 0.75,
              px: 0.5,
              mb: 0.75,
              borderRadius: '8px !important',
              textTransform: 'none !important',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0,0,0,0.5)',
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
              }}
            >
              Session breakdown
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'rgba(0,0,0,0.45)',
                transform: sessionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </ButtonBase>

          <Collapse in={sessionsOpen}>
            <Box
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                bgcolor: '#fff',
                overflow: 'hidden',
                mb: 1,
              }}
            >
          {(() => {
            const interactive = sessions.filter(
              (s) => s.hasEngagement && s.duration != null && s.duration > 5000
            );

            if (interactive.length === 0) {
              return (
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)', py: 2, textAlign: 'center' }}>
                  No session details available.
                </Typography>
              );
            }

            const renderSessionRow = (s, idx, isLast) => {
              const label =
                s.key && s.key.includes('@') ? s.key : `guest · ${s.key?.slice(0, 8) ?? '—'}`;
              const durationStr = formatSessionDuration(s.duration);
              const actionParts = [
                s.actionLabels.includes('auto-furnished') ? 'auto-furnished' : null,
                s.shuffleCount > 0
                  ? `${s.shuffleCount} shuffle${s.shuffleCount !== 1 ? 's' : ''}`
                  : null,
                s.actionLabels.includes('added items') ? 'added items' : null,
              ].filter(Boolean);
              const actionsStr = actionParts.length > 0 ? actionParts.join(' · ') : 'viewed only';

              return (
                <Box
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: 'rgba(0,0,0,0.78)',
                          lineHeight: 1.25,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)', mt: 0.2 }}>
                        {actionsStr}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography
                        sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(0,0,0,0.65)', lineHeight: 1.25 }}
                      >
                        {durationStr}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(0,0,0,0.35)', mt: 0.2 }}>
                        {formatTimestamp(s.openTs)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            };

            // All buckets render the same way now. The previous special
            // "outlier" collapsed-by-default branch existed only because the
            // top bucket was 30+ minutes wall-clock and could plausibly
            // include idle time. With the new umbrella-visit model + 2-minute
            // idle subtraction (now applied uniformly) the 15+ min bucket is
            // genuine engagement, not a flag.
            return SESSION_BUCKETS.map((bucket) => {
              const totalInBucket = bucketCounts?.[bucket.key] ?? 0;
              if (totalInBucket === 0) return null;

              const rowsInSlice = interactive.filter((s) => s.bucket === bucket.key);
              const sliceCount = rowsInSlice.length;
              const totalText = `${totalInBucket} session${totalInBucket !== 1 ? 's' : ''}`;

              return (
                <Box key={bucket.key} sx={{ mb: 1.25 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{
                      py: 0.75,
                      px: 0.5,
                      borderRadius: '6px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '999px',
                        bgcolor: BUCKET_COLORS[bucket.key],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: 'rgba(0,0,0,0.72)',
                      }}
                    >
                      {bucket.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(0,0,0,0.38)' }}>
                      {bucket.range}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'rgba(0,0,0,0.5)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {totalText}
                    </Typography>
                  </Stack>

                  {sliceCount === 0 ? (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'rgba(0,0,0,0.35)',
                        px: 0.5,
                        py: 0.75,
                      }}
                    >
                      No recent sessions in this range (showing 30 most recent overall).
                    </Typography>
                  ) : (
                    rowsInSlice.map((s, i) => (
                      <React.Fragment key={`${bucket.key}-${i}`}>
                        {renderSessionRow(s, i, i === rowsInSlice.length - 1)}
                      </React.Fragment>
                    ))
                  )}
                  {sliceCount > 0 && sliceCount < totalInBucket && (
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        color: 'rgba(0,0,0,0.35)',
                        px: 0.5,
                        pt: 0.5,
                        fontStyle: 'italic',
                      }}
                    >
                      Showing {sliceCount} of {totalInBucket} most recent in this range.
                    </Typography>
                  )}
                </Box>
              );
            });
          })()}
            </Box>
          </Collapse>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default EditorEngagementCard;
